import { useState, useEffect } from 'react';
import { X, Save, FileText, Zap, Target, TrendingUp, AlertTriangle, Mail, TrendingDown, DollarSign, Clock, Loader2, Calendar } from 'lucide-react';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { Checkbox } from '@/components/ui/checkbox';
import { useTableSort } from '@/hooks/useTableSort';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import type { CollectionStrategyItem, StrategyAction, TriggerConfig, AssignmentRule } from '@/types/collection-strategies';
import type { StrategyDiagnostics } from '@/types/strategy-diagnostics';
import { StrategyStageEditor } from '@/components/settings/strategy-stage-editor';
import { generateMockDiagnostics } from '@/lib/mock-data/strategy-diagnostics-mock-data';
import { cn } from '@/lib/utils';
import { useStrategyById, useCreateStrategy, useUpdateStrategy, useStrategyTypeOptions } from '@/hooks/useStrategiesData';

export function StrategyEditorPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { strategyId: strategyIdParam } = useParams<{ strategyId: string }>();

    // Get strategy and mode from location state or URL params
    const strategyFromState = location.state?.strategy as CollectionStrategyItem | null;
    const strategyId = strategyIdParam || searchParams.get('id') || strategyFromState?.id || null;
    const mode = (searchParams.get('mode') || location.state?.mode || (strategyId ? 'edit' : 'create')) as 'create' | 'edit';

    // Fetch strategy data from API when editing (for live data mode)
    // Pass strategyId directly (can be string or number)
    const { data: strategyFromApi, isLoading: isLoadingStrategy } = useStrategyById(strategyId);
    
    // Use API data if available, otherwise fall back to state data
    const strategy = strategyFromApi || strategyFromState;

    // Mutations for create and update
    const createStrategyMutation = useCreateStrategy();
    // For update, try to parse as number, otherwise use the string ID
    const strategyIdForUpdate = strategyId ? (isNaN(Number(strategyId)) ? strategyId : parseInt(strategyId, 10)) : 0;
    const updateStrategyMutation = useUpdateStrategy(strategyIdForUpdate);

    // Lookup data for dropdowns (cached for 1 minute)
    const { data: strategyTypeOptions = [] } = useStrategyTypeOptions();

    const [formData, setFormData] = useState({
        name: '',
        type: 'Standard' as CollectionStrategyItem['type'],
        description: '',
        status: 'Active' as 'Active' | 'Inactive',
        triggerType: 'days_overdue' as TriggerConfig['type'],
        daysOverdue: 30,
        amountThreshold: 5000,
        riskScoreThreshold: 70,
        actions: [] as StrategyAction[],
        assignmentRules: [] as AssignmentRule[],
        aiEnabled: false,
        aiTone: 3,
        aiFormality: 'professional' as 'casual' | 'professional' | 'formal',
        aiPersonalization: 'moderate' as 'minimal' | 'moderate' | 'deep',
        aiContextFactors: {
            paymentHistory: true,
            riskProfile: true,
            relationshipDuration: false,
            industryContext: false,
            economicFactors: false,
            previousDisputes: false,
            communicationPreferences: false,
        },
        aiTimingOptimization: true,
        // Automation Frequency fields
        automated: 'No' as 'Yes' | 'No',
        automationTime: '09:00',
        automationDays: {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false,
        },
    });

    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
    
    // Get initial tab from URL parameter, default to 'performance' if coming from dashboard
    const initialTab = searchParams.get('tab') || 'settings';
    const [activeTab, setActiveTab] = useState(initialTab);
    const [diagnostics, setDiagnostics] = useState<StrategyDiagnostics | null>(null);

    // Initialize form data when strategy changes
    useEffect(() => {
        if (strategy && mode === 'edit') {
            setFormData({
                name: strategy.name,
                type: strategy.type,
                description: strategy.description,
                status: strategy.status,
                triggerType: strategy.trigger.type,
                daysOverdue: strategy.trigger.daysOverdue || 30,
                amountThreshold: strategy.trigger.amountThreshold || 5000,
                riskScoreThreshold: strategy.trigger.riskScoreThreshold || 70,
                actions: strategy.actions,
                assignmentRules: strategy.assignmentRules,
                aiEnabled: !!strategy.aiBehavior?.enabled,
                aiTone: strategy.aiBehavior?.communicationTone || 3,
                aiFormality: strategy.aiBehavior?.formalityLevel || 'professional',
                aiPersonalization: strategy.aiBehavior?.personalizationDepth || 'moderate',
                aiContextFactors: strategy.aiBehavior?.contextFactors || {
                    paymentHistory: true,
                    riskProfile: true,
                    relationshipDuration: false,
                    industryContext: false,
                    economicFactors: false,
                    previousDisputes: false,
                    communicationPreferences: false,
                },
                aiTimingOptimization: strategy.aiBehavior?.timingOptimization || true,
                // Automation Frequency fields - use defaults if not present
                automated: 'No',
                automationTime: '09:00',
                automationDays: {
                    monday: false,
                    tuesday: false,
                    wednesday: false,
                    thursday: false,
                    friday: false,
                    saturday: false,
                    sunday: false,
                },
            });
            
            // Load diagnostics for edit mode
            const mockDiagnostics = generateMockDiagnostics();
            setDiagnostics(mockDiagnostics[strategy.id] || null);
        } else {
            setFormData({
                name: '',
                type: 'Standard',
                description: '',
                status: 'Active',
                triggerType: 'days_overdue',
                daysOverdue: 30,
                amountThreshold: 5000,
                riskScoreThreshold: 70,
                actions: [],
                assignmentRules: [],
                aiEnabled: false,
                aiTone: 3,
                aiFormality: 'professional',
                aiPersonalization: 'moderate',
                aiContextFactors: {
                    paymentHistory: true,
                    riskProfile: true,
                    relationshipDuration: false,
                    industryContext: false,
                    economicFactors: false,
                    previousDisputes: false,
                    communicationPreferences: false,
                },
                aiTimingOptimization: true,
                // Automation Frequency fields
                automated: 'No',
                automationTime: '09:00',
                automationDays: {
                    monday: false,
                    tuesday: false,
                    wednesday: false,
                    thursday: false,
                    friday: false,
                    saturday: false,
                    sunday: false,
                },
            });
            setDiagnostics(null);
        }
    }, [strategy, mode]);

    // Check if there are unsaved changes
    const hasUnsavedChanges = () => {
        if (mode === 'create') {
            return formData.name !== '' || formData.description !== '';
        }
        if (strategy) {
            return (
                formData.name !== strategy.name ||
                formData.type !== strategy.type ||
                formData.description !== strategy.description ||
                formData.status !== strategy.status ||
                formData.triggerType !== strategy.trigger.type ||
                formData.daysOverdue !== (strategy.trigger.daysOverdue || 30) ||
                formData.amountThreshold !== (strategy.trigger.amountThreshold || 5000) ||
                formData.riskScoreThreshold !== (strategy.trigger.riskScoreThreshold || 70)
            );
        }
        return false;
    };

    const handleSave = async () => {
        // Basic validation
        if (!formData.name.trim()) {
            toast.error('Please enter a strategy name');
            return;
        }
        if (!formData.description.trim()) {
            toast.error('Please enter a strategy description');
            return;
        }

        // Build the strategy object from form data
        const strategyData: CollectionStrategyItem = {
            id: strategy?.id || '0',
            name: formData.name,
            type: formData.type,
            description: formData.description,
            status: formData.status,
            trigger: {
                type: formData.triggerType,
                daysOverdue: formData.daysOverdue,
                amountThreshold: formData.amountThreshold,
                riskScoreThreshold: formData.riskScoreThreshold,
            },
            actions: formData.actions,
            assignmentRules: formData.assignmentRules,
            aiBehavior: formData.aiEnabled ? {
                enabled: formData.aiEnabled,
                communicationTone: formData.aiTone as 1 | 2 | 3 | 4 | 5,
                formalityLevel: formData.aiFormality,
                personalizationDepth: formData.aiPersonalization,
                contextFactors: formData.aiContextFactors,
                timingOptimization: formData.aiTimingOptimization,
                escalationTriggers: strategy?.aiBehavior?.escalationTriggers || {
                    noResponseDays: 7,
                    brokenPromises: 2,
                    disputeRaised: true,
                },
                humanReviewRequired: strategy?.aiBehavior?.humanReviewRequired ?? true,
            } : undefined,
            activeCasesCount: strategy?.activeCasesCount || 0,
            successRate: strategy?.successRate || 0,
            avgDaysToPayment: strategy?.avgDaysToPayment || 0,
            performanceMetrics: strategy?.performanceMetrics || {
                totalCasesProcessed: 0,
                successfulResolutions: 0,
                averageResolutionTime: 0,
                collectionRate: 0,
            },
            createdAt: strategy?.createdAt || new Date(),
            updatedAt: new Date(),
        };

        try {
            if (mode === 'create') {
                await createStrategyMutation.mutateAsync(strategyData);
                toast.success('Strategy created successfully');
            } else {
                await updateStrategyMutation.mutateAsync(strategyData);
                toast.success('Strategy updated successfully');
            }
            navigate('/settings/strategies');
        } catch (error) {
            toast.error(mode === 'create' ? 'Failed to create strategy' : 'Failed to update strategy');
            console.error('Save strategy error:', error);
        }
    };

    const handleCancel = () => {
        if (hasUnsavedChanges()) {
            setPendingNavigation('/settings/strategies');
            setShowUnsavedChangesDialog(true);
        } else {
            navigate('/settings/strategies');
        }
    };

    const confirmNavigation = () => {
        if (pendingNavigation) {
            navigate(pendingNavigation);
        }
        setShowUnsavedChangesDialog(false);
        setPendingNavigation(null);
    };

    const cancelNavigation = () => {
        setShowUnsavedChangesDialog(false);
        setPendingNavigation(null);
    };

    const handleActionsChange = (actions: StrategyAction[]) => {
        setFormData({
            ...formData,
            actions,
        });
    };

    const breadcrumbItems = [
        { label: 'Settings', href: '/settings/strategies' },
        { label: 'Collection Strategies', href: '/settings/strategies' },
        { label: mode === 'create' ? 'Create Strategy' : 'Edit Strategy' },
    ];

    // Helper functions for Performance tab
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const getEventColor = (event: string) => {
        switch (event) {
            case 'deactivated':
                return 'bg-red-500';
            case 'activated':
            case 'run_completed':
                return 'bg-green-500';
            case 'created':
                return 'bg-blue-500';
            case 'configuration_changed':
            case 'updated':
                return 'bg-orange-500';
            default:
                return 'bg-gray-400';
        }
    };

    const formatEventName = (event: string) => {
        return event
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Audit trail sorting - transform data to make user sortable
    const auditTrailData = (diagnostics?.auditTrail || []).map(item => ({
        ...item,
        userName: item.user.name, // Add flat property for sorting
    }));
    const { sortedData: sortedAuditTrail, requestSort, getSortDirection } = useTableSort(
        auditTrailData,
        { initialSortKey: 'date', initialSortDirection: 'desc' }
    );

    const hasIssues = diagnostics && (diagnostics.emailsSent14Days === 0 || diagnostics.status === 'Inactive');
    const isActive = diagnostics?.status === 'Active';

    return (
        <div className="flex-1 flex flex-col h-screen overflow-hidden">
            {/* Header with Breadcrumb */}
            <div className="border-b bg-background px-8 py-4">
                <div className="flex items-center justify-between mb-3">
                    <Breadcrumb items={breadcrumbItems} />
                </div>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">
                            {mode === 'create' ? 'Create Strategy' : `Edit Strategy: ${strategy?.name}`}
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {mode === 'create'
                                ? 'Create a new collection strategy with automated workflows'
                                : 'Modify the strategy settings and configuration'
                            }
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={handleCancel}>
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleSave} 
                            disabled={!formData.name || !formData.description || createStrategyMutation.isPending || updateStrategyMutation.isPending || (isLoadingStrategy && mode === 'edit')}
                        >
                            {(createStrategyMutation.isPending || updateStrategyMutation.isPending) ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="h-4 w-4 mr-2" />
                            )}
                            {mode === 'create' ? 'Create Strategy' : 'Save Changes'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Main Content - Tabbed Layout */}
            <div className="flex-1 overflow-y-auto">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                    <div className="border-b bg-background px-8">
                        <TabsList className="h-12">
                            <TabsTrigger value="settings" className="gap-2">
                                <FileText className="h-4 w-4" />
                                Settings
                            </TabsTrigger>
                            {mode === 'edit' && (
                                <TabsTrigger value="performance" className="gap-2">
                                    <TrendingUp className="h-4 w-4" />
                                    Performance
                                </TabsTrigger>
                            )}
                        </TabsList>
                    </div>

                    {/* Settings Tab */}
                    <TabsContent value="settings" className="flex-1 overflow-y-auto px-8 py-6 mt-0">
                        {isLoadingStrategy && mode === 'edit' ? (
                            <div className="flex items-center justify-center py-16">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                <span className="ml-2 text-muted-foreground">Loading strategy...</span>
                            </div>
                        ) : (
                        <div className="max-w-7xl mx-auto space-y-6">
                    {/* Row 1: Overview and Triggers Side-by-Side */}
                    <div className="grid grid-cols-2 gap-6">
                        {/* Panel 1: Overview */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="h-5 w-5 text-blue-500" />
                                    Overview
                                </CardTitle>
                                <CardDescription>
                                    Basic information about the collection strategy
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label>Strategy name *</Label>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g., Standard 30-Day Collection"
                                    />
                                </div>
                                <div>
                                    <Label>Type *</Label>
                                    <Select
                                        value={formData.type}
                                        onValueChange={(value: CollectionStrategyItem['type']) => setFormData({ ...formData, type: value })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {strategyTypeOptions.length > 0 ? (
                                                strategyTypeOptions.map((option) => (
                                                    <SelectItem key={option.id} value={option.name}>
                                                        {option.name}
                                                    </SelectItem>
                                                ))
                                            ) : (
                                                <>
                                                    <SelectItem value="Standard">Standard</SelectItem>
                                                    <SelectItem value="Intensive">Intensive</SelectItem>
                                                    <SelectItem value="VIP">VIP</SelectItem>
                                                    <SelectItem value="High Risk">High Risk</SelectItem>
                                                </>
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Description *</Label>
                                    <Textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Describe when and how this strategy should be used..."
                                        rows={4}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Panel 2: Triggers */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Zap className="h-5 w-5 text-amber-500" />
                                    Triggers
                                </CardTitle>
                                <CardDescription>
                                    Define when this strategy should be automatically applied
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label>Trigger Type</Label>
                                    <Select
                                        value={formData.triggerType}
                                        onValueChange={(value: 'days_overdue' | 'amount_threshold' | 'risk_score' | 'manual_only' | 'payment_pattern_change' | 'promise_broken') => setFormData({ ...formData, triggerType: value })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="days_overdue">Days Overdue</SelectItem>
                                            <SelectItem value="amount_threshold">Amount Threshold</SelectItem>
                                            {/* <SelectItem value="risk_score">Risk Score</SelectItem> */}
                                            {/* <SelectItem value="manual_only">Manual Only</SelectItem> */}
                                            {/* <SelectItem value="payment_pattern_change">Payment Pattern Change</SelectItem> */}
                                            {/* <SelectItem value="promise_broken">Promise Broken</SelectItem> */}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {formData.triggerType === 'days_overdue' && (
                                    <div>
                                        <Label>Days Overdue Threshold</Label>
                                        <Input
                                            type="number"
                                            value={formData.daysOverdue}
                                            onChange={(e) => setFormData({ ...formData, daysOverdue: parseInt(e.target.value) || 0 })}
                                        />
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Trigger when invoice is overdue by this many days
                                        </p>
                                    </div>
                                )}

                                {formData.triggerType === 'amount_threshold' && (
                                    <div>
                                        <Label>Amount Threshold ($)</Label>
                                        <Input
                                            type="number"
                                            value={formData.amountThreshold}
                                            onChange={(e) => setFormData({ ...formData, amountThreshold: parseInt(e.target.value) || 0 })}
                                        />
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Trigger when outstanding amount exceeds this value
                                        </p>
                                    </div>
                                )}

                                {formData.triggerType === 'risk_score' && (
                                    <div>
                                        <Label>Risk Score Threshold</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={formData.riskScoreThreshold}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value) || 0;
                                                setFormData({ ...formData, riskScoreThreshold: Math.min(100, Math.max(0, value)) });
                                            }}
                                        />
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Trigger when customer risk score reaches or exceeds this value (0-100)
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Panel 3: Stages */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Target className="h-5 w-5 text-green-500" />
                                Stages
                            </CardTitle>
                            <CardDescription>
                                Configure the collection actions to be performed
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <StrategyStageEditor
                                actions={formData.actions}
                                onActionsChange={handleActionsChange}
                            />
                        </CardContent>
                    </Card>

                    {/* Panel 4: Automation Frequency */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Calendar className="h-5 w-5 text-purple-500" />
                                Automation Frequency
                            </CardTitle>
                            <CardDescription>
                                Configure when this strategy should run automatically
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Field 1: Automated */}
                            <div>
                                <Label>Automated *</Label>
                                <Select
                                    value={formData.automated}
                                    onValueChange={(value: 'Yes' | 'No') => setFormData({ ...formData, automated: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="No">No</SelectItem>
                                        <SelectItem value="Yes">Yes</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Enable automatic execution of this strategy
                                </p>
                            </div>

                            {/* Field 2: Time */}
                            <div>
                                <Label>Time</Label>
                                <Select
                                    value={formData.automationTime}
                                    onValueChange={(value: string) => setFormData({ ...formData, automationTime: value })}
                                    disabled={formData.automated === 'No'}
                                >
                                    <SelectTrigger className={formData.automated === 'No' ? 'opacity-50' : ''}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="00:00">12:00 AM</SelectItem>
                                        <SelectItem value="01:00">1:00 AM</SelectItem>
                                        <SelectItem value="02:00">2:00 AM</SelectItem>
                                        <SelectItem value="03:00">3:00 AM</SelectItem>
                                        <SelectItem value="04:00">4:00 AM</SelectItem>
                                        <SelectItem value="05:00">5:00 AM</SelectItem>
                                        <SelectItem value="06:00">6:00 AM</SelectItem>
                                        <SelectItem value="07:00">7:00 AM</SelectItem>
                                        <SelectItem value="08:00">8:00 AM</SelectItem>
                                        <SelectItem value="09:00">9:00 AM</SelectItem>
                                        <SelectItem value="10:00">10:00 AM</SelectItem>
                                        <SelectItem value="11:00">11:00 AM</SelectItem>
                                        <SelectItem value="12:00">12:00 PM</SelectItem>
                                        <SelectItem value="13:00">1:00 PM</SelectItem>
                                        <SelectItem value="14:00">2:00 PM</SelectItem>
                                        <SelectItem value="15:00">3:00 PM</SelectItem>
                                        <SelectItem value="16:00">4:00 PM</SelectItem>
                                        <SelectItem value="17:00">5:00 PM</SelectItem>
                                        <SelectItem value="18:00">6:00 PM</SelectItem>
                                        <SelectItem value="19:00">7:00 PM</SelectItem>
                                        <SelectItem value="20:00">8:00 PM</SelectItem>
                                        <SelectItem value="21:00">9:00 PM</SelectItem>
                                        <SelectItem value="22:00">10:00 PM</SelectItem>
                                        <SelectItem value="23:00">11:00 PM</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Select the time for automation execution
                                </p>
                            </div>

                            {/* Field 3: Days of the Week */}
                            <div>
                                <Label>Days of the Week</Label>
                                <div className="mt-2 space-y-2">
                                    {[
                                        { key: 'monday', label: 'Monday' },
                                        { key: 'tuesday', label: 'Tuesday' },
                                        { key: 'wednesday', label: 'Wednesday' },
                                        { key: 'thursday', label: 'Thursday' },
                                        { key: 'friday', label: 'Friday' },
                                        { key: 'saturday', label: 'Saturday' },
                                        { key: 'sunday', label: 'Sunday' },
                                    ].map((day) => (
                                        <div key={day.key} className="flex items-center space-x-2">
                                            <Checkbox
                                                id={day.key}
                                                checked={formData.automationDays[day.key as keyof typeof formData.automationDays]}
                                                onCheckedChange={(checked) => {
                                                    setFormData({
                                                        ...formData,
                                                        automationDays: {
                                                            ...formData.automationDays,
                                                            [day.key]: checked === true,
                                                        },
                                                    });
                                                }}
                                                disabled={formData.automated === 'No'}
                                                className={formData.automated === 'No' ? 'opacity-50' : ''}
                                            />
                                            <label
                                                htmlFor={day.key}
                                                className={cn(
                                                    'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
                                                    formData.automated === 'No' && 'opacity-50 cursor-not-allowed'
                                                )}
                                            >
                                                {day.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-sm text-muted-foreground mt-2">
                                    Select which days the strategy should run
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                        </div>
                        )}
                    </TabsContent>

                    {/* Performance Tab */}
                    {mode === 'edit' && diagnostics && (
                        <TabsContent value="performance" className="flex-1 overflow-y-auto px-8 py-6 mt-0">
                            <div className="max-w-7xl mx-auto space-y-6">
                                {/* Warning Banner */}
                                {hasIssues && (
                                    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4">
                                        <div className="flex items-start gap-3">
                                            <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                                            <div className="flex-1">
                                                <h4 className="text-base font-semibold text-amber-900 mb-1">
                                                    Strategy Issue Detected
                                                </h4>
                                                <p className="text-sm text-amber-800 mb-2">
                                                    {diagnostics.impactAnalysis.notReceivingEmails
                                                        ? `${diagnostics.impactAnalysis.notReceivingEmails.count} customers did not receive follow-up emails`
                                                        : 'This strategy requires attention'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Status Overview and Impact Analysis - Side by Side */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Status Overview Card */}
                                    <Card className="flex flex-col">
                                        <CardHeader>
                                            <CardTitle className="flex items-center gap-2">
                                                <Target className="h-5 w-5 text-teal-600" />
                                                Status Overview
                                            </CardTitle>
                                            <CardDescription>
                                                Current status and key metrics
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent className="flex-1">
                                            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                                {/* Created */}
                                                <div className="space-y-0.5">
                                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Created</p>
                                                    <p className="text-sm font-semibold text-gray-900">
                                                        {format(diagnostics.createdAt, 'dd MMM yyyy')}
                                                    </p>
                                                    <p className="text-xs text-gray-600">
                                                        by {diagnostics.createdBy.name}
                                                    </p>
                                                </div>

                                                {/* Active Accounts */}
                                                <div className="space-y-0.5">
                                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Active Accounts</p>
                                                    <div className="flex items-center gap-2">
                                                        <Target className="h-5 w-5 text-teal-600" />
                                                        <p className="text-2xl font-bold text-gray-900">
                                                            {diagnostics.activeAccounts}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Last Run */}
                                                <div className="space-y-0.5">
                                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Last Run</p>
                                                    {diagnostics.lastRun ? (
                                                        <>
                                                            <p className="text-sm font-semibold text-gray-900">
                                                                {format(diagnostics.lastRun, 'dd MMM yyyy, h:mmaaa')}
                                                            </p>
                                                            <p className="text-xs text-gray-600">
                                                                {formatDistanceToNow(diagnostics.lastRun, { addSuffix: true })}
                                                            </p>
                                                        </>
                                                    ) : (
                                                        <p className="text-sm text-gray-400">—</p>
                                                    )}
                                                </div>

                                                {/* Emails Sent */}
                                                <div className="space-y-0.5">
                                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Emails Sent (14 Days)</p>
                                                    <div className="flex items-center gap-2">
                                                        {diagnostics.emailsSent14Days === 0 && (
                                                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                                                        )}
                                                        <p className={cn(
                                                            'text-2xl font-bold',
                                                            diagnostics.emailsSent14Days === 0 ? 'text-red-600' : 'text-gray-900'
                                                        )}>
                                                            {diagnostics.emailsSent14Days}
                                                        </p>
                                                    </div>
                                                    {diagnostics.expectedEmails && (
                                                        <p className="text-xs text-gray-600">
                                                            Expected: ~{diagnostics.expectedEmails}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Next Scheduled Run */}
                                                <div className="space-y-0.5">
                                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Next Scheduled Run</p>
                                                    {diagnostics.nextScheduledRun && isActive ? (
                                                        <>
                                                            <p className="text-sm font-semibold text-gray-900">
                                                                {format(diagnostics.nextScheduledRun, 'dd MMM yyyy, h:mmaaa')}
                                                            </p>
                                                            <p className="text-xs text-green-600">
                                                                {formatDistanceToNow(diagnostics.nextScheduledRun, { addSuffix: true })}
                                                            </p>
                                                        </>
                                                    ) : (
                                                        <p className="text-sm text-gray-400">—</p>
                                                    )}
                                                </div>

                                                {/* Success Rate */}
                                                <div className="space-y-1">
                                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Success Rate</p>
                                                    <p className="text-2xl font-bold text-gray-900">{diagnostics.successRate}%</p>
                                                    <Progress value={diagnostics.successRate} className="h-2" />
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {/* Impact Analysis Card */}
                                    <Card className="flex flex-col">
                                        <CardHeader>
                                            <CardTitle className="flex items-center gap-2">
                                                <TrendingUp className="h-5 w-5 text-green-600" />
                                                Impact Analysis
                                            </CardTitle>
                                            <CardDescription>
                                                Customers affected by strategy
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent className="flex-1 space-y-3">
                                            {/* Not Receiving Emails */}
                                            {diagnostics.impactAnalysis.notReceivingEmails && (
                                                <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-3">
                                                    <div className="flex items-start gap-2">
                                                        <Mail className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-red-900 mb-0.5">
                                                                {diagnostics.impactAnalysis.notReceivingEmails.count} customers not receiving emails
                                                            </p>
                                                            <p className="text-xs text-gray-700 break-words">
                                                                {diagnostics.impactAnalysis.notReceivingEmails.reason}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* At Risk Accounts */}
                                            {diagnostics.impactAnalysis.atRiskAccounts && (
                                                <div className="bg-orange-50 border-l-4 border-orange-500 rounded-lg p-3">
                                                    <div className="flex items-start gap-2">
                                                        <TrendingDown className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-orange-900 mb-0.5">
                                                                {diagnostics.impactAnalysis.atRiskAccounts.count} accounts at elevated risk
                                                            </p>
                                                            <p className="text-xs text-gray-700 break-words">
                                                                {diagnostics.impactAnalysis.atRiskAccounts.description}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Successful Contacts */}
                                            {diagnostics.impactAnalysis.successfulContacts && (
                                                <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-3">
                                                    <div className="flex items-start gap-2">
                                                        <Mail className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-green-900 mb-0.5">
                                                                {diagnostics.impactAnalysis.successfulContacts.count} accounts successfully contacted
                                                            </p>
                                                            <p className="text-xs text-gray-700 break-words">
                                                                {diagnostics.impactAnalysis.successfulContacts.description}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Total Exposure */}
                                            {diagnostics.impactAnalysis.totalExposure && (
                                                <div className={cn(
                                                    'border-l-4 rounded-lg p-3',
                                                    diagnostics.impactAnalysis.totalExposure.severity === 'high'
                                                        ? 'bg-amber-50 border-amber-500'
                                                        : 'bg-green-50 border-green-500'
                                                )}>
                                                    <div className="flex items-start gap-2">
                                                        <DollarSign className={cn(
                                                            'h-4 w-4 flex-shrink-0 mt-0.5',
                                                            diagnostics.impactAnalysis.totalExposure.severity === 'high'
                                                                ? 'text-amber-600'
                                                                : 'text-green-600'
                                                        )} />
                                                        <div className="flex-1 min-w-0">
                                                            <p className={cn(
                                                                'text-sm font-semibold mb-0.5',
                                                                diagnostics.impactAnalysis.totalExposure.severity === 'high'
                                                                    ? 'text-amber-900'
                                                                    : 'text-green-900'
                                                            )}>
                                                                {formatCurrency(diagnostics.impactAnalysis.totalExposure.amount)} total outstanding
                                                            </p>
                                                            <p className="text-xs text-gray-700">
                                                                Avg: {formatCurrency(diagnostics.impactAnalysis.totalExposure.averagePerAccount)} per account
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Audit Trail Card */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Clock className="h-5 w-5 text-blue-600" />
                                            Audit Trail
                                        </CardTitle>
                                        <CardDescription>
                                            Recent changes and actions
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <SortableTableHead
                                                        sortKey="date"
                                                        currentSortDirection={getSortDirection('date')}
                                                        onSort={() => requestSort('date')}
                                                    >
                                                        Date & Time
                                                    </SortableTableHead>
                                                    <SortableTableHead
                                                        sortKey="event"
                                                        currentSortDirection={getSortDirection('event')}
                                                        onSort={() => requestSort('event')}
                                                    >
                                                        Event Type
                                                    </SortableTableHead>
                                                    <SortableTableHead
                                                        sortKey="userName"
                                                        currentSortDirection={getSortDirection('userName')}
                                                        onSort={() => requestSort('userName')}
                                                    >
                                                        User
                                                    </SortableTableHead>
                                                    <TableHead>Details</TableHead>
                                                    <TableHead>Reason</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {sortedAuditTrail.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                                                            No audit trail entries found
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    sortedAuditTrail.map((item) => (
                                                        <TableRow key={item.id}>
                                                            <TableCell className="whitespace-nowrap">
                                                                <div className="flex flex-col">
                                                                    <span className="text-sm font-medium text-gray-900">
                                                                        {format(item.date, 'dd MMM yyyy')}
                                                                    </span>
                                                                    <span className="text-xs text-gray-500">
                                                                        {format(item.date, 'h:mm aaa')}
                                                                    </span>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                <div className="flex items-center gap-2">
                                                                    <div
                                                                        className={cn(
                                                                            'w-2 h-2 rounded-full flex-shrink-0',
                                                                            getEventColor(item.event)
                                                                        )}
                                                                    />
                                                                    <span className="text-sm font-medium text-gray-900">
                                                                        {formatEventName(item.event)}
                                                                    </span>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="whitespace-nowrap">
                                                                <span className="text-sm text-gray-900">
                                                                    {item.user.name}
                                                                </span>
                                                            </TableCell>
                                                            <TableCell>
                                                                {item.details ? (
                                                                    <span className="text-sm text-gray-600 break-words">
                                                                        {item.details}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-sm text-gray-400">—</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {item.reason ? (
                                                                    <span className="text-sm text-gray-600 italic break-words">
                                                                        {item.reason}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-sm text-gray-400">—</span>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </Card>
                            </div>
                        </TabsContent>
                    )}
                </Tabs>
            </div>

            {/* Unsaved Changes Dialog */}
            <AlertDialog open={showUnsavedChangesDialog} onOpenChange={setShowUnsavedChangesDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={cancelNavigation}>Stay on Page</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmNavigation}>Leave Without Saving</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default StrategyEditorPage;
